// The model one Playground client runs, chosen on that client's own column.
// Lists only the models that client's provider family can actually run
// (`modelsForClient`), so a Claude column never offers a GPT model.
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@sphinx/design-system/dropdown-menu";
import { cn } from "@/lib/utils";
import { modelsForClient } from "@/lib/playground-client-models";
import { getModelById } from "@/shared/types";
import type { SphinxModel } from "@/shared/sphinx";

export const modelLabel = (model: string): string =>
  getModelById(model)?.name ?? model;

interface PlaygroundClientModelPickerProps {
  target: string;
  model: SphinxModel | null;
  onModelChange: (model: SphinxModel) => void;
  className?: string;
}

export function PlaygroundClientModelPicker({
  target,
  model,
  onModelChange,
  className,
}: PlaygroundClientModelPickerProps) {
  const options = modelsForClient(target);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex max-w-[9rem] items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground",
            className
          )}
          data-testid={`playground-client-model-${target}`}
          data-model={model ?? ""}
          aria-label={`Model for ${target}`}
        >
          <span className="truncate">
            {model ? modelLabel(model) : "Select model"}
          </span>
          <ChevronDown className="size-3 shrink-0" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        {options.map((option) => (
          <DropdownMenuItem
            key={option}
            onSelect={() => onModelChange(option)}
            data-testid={`playground-client-model-option-${target}-${option}`}
            className={cn("text-xs", option === model && "font-semibold")}
          >
            {modelLabel(option)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
